import Holidays from 'date-holidays';
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import _ from 'lodash';

dayjs.extend(dayjsBusinessDays);

/**
 * Lists the current year's holidays for a country, used to skip non-working days in scheduling. Initialises a Holidays instance for the country, applies the timezone and requests the holidays of the year returned by dayjs.
 *
 * @param country - Country code whose holiday calendar is used.
 * @returns Holiday entries for the current year.
 */
export const getHolidays = ({ country = 'US', timezone }: any = {}): any => {
  const hd = new Holidays();
  hd.init(country);
  hd.setTimezone(timezone);
  // *: Get holidays in current year
  const holidays = hd.getHolidays(dayjs().year());
  return holidays;
};

/**
 * Reports the holidays falling on a given date for a country, used when computing study and shipping schedules. Initialises a Holidays instance for the country, applies the timezone and delegates to its isHoliday check.
 *
 * @param date - Date to test for holidays.
 * @param country - Country code whose holiday calendar is used.
 * @returns Array of holidays on that date, or false when none fall on it.
 */
export const checksIsHoliday = ({ date, country = 'US', timezone }: any = {}): any => {
  const hd = new Holidays();
  hd.init(country);
  hd.setTimezone(timezone);
  // *: Return an arrays of holidays in specify date
  return hd.isHoliday(date);
};

/**
 * Measures how many days a run of holidays spans so schedules can be shifted around it. Takes the earliest start and latest end across the holiday entries with lodash minBy and maxBy over dayjs dates and returns their difference in days, or zero for an empty list.
 *
 * @param holidays - Holiday entries with start and end dates.
 * @returns Span between the earliest start and latest end, in days.
 */
export const getHolidayDuration = (holidays: any) => {
  let holidayDuration = 0;
  if (holidays.length) {
    // *: Find a range of holidays (from min date to max date)
    /**
     * Computes the holiday with the earliest start date from the list. Derived via lodash minBy comparing each holiday's start date parsed with dayjs.
     *
     * @returns The holiday entry having the minimum start date.
     */
    const minDateHoliday = _.minBy(holidays, (holiday: any) => dayjs(holiday.start));
    /**
     * Computes the holiday with the latest end date from the list. Derived via lodash maxBy comparing each holiday's end date parsed with dayjs.
     *
     * @returns The holiday entry having the maximum end date.
     */
    const maxDateHoliday = _.maxBy(holidays, (holiday: any) => dayjs(holiday.end));
    // *: Calculate duration of min start holiday and max end holiday
    holidayDuration = dayjs(maxDateHoliday.end).diff(dayjs(minDateHoliday.start), 'day');
  }
  return holidayDuration;
};
